import { useState, useEffect } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Crown, Mic } from "lucide-react";
import { toast } from "sonner";

export function FanClubPanel() {
  const [members, setMembers] = useState([]);
  const [paidCount, setPaidCount] = useState(0);
  const [fanMail, setFanMail] = useState([]);
  const [winnerData, setWinnerData] = useState(null);
  const [winners, setWinners] = useState([]);
  const [busy, setBusy] = useState(false);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const load = async () => {
    try {
      const [m, fm, w, hist] = await Promise.all([
        api.get("/admin/fan-club/members"),
        api.get("/admin/fan-club/fan-mail"),
        api.get(`/admin/fan-club/winner/current?month=${month}`),
        api.get("/admin/fan-club/winners"),
      ]);
      setMembers(m.data.members || []);
      setPaidCount(m.data.paid_count || 0);
      setFanMail(fm.data.fan_mail || []);
      setWinnerData(w.data);
      setWinners(hist.data.winners || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Could not load fan-club data");
    }
  };

  useEffect(() => {
    const id = setTimeout(load, 0);
    return () => clearTimeout(id);
  }, [month]);

  const selectWinner = async (email, override) => {
    setBusy(true);
    try {
      await api.post("/admin/fan-club/winner/select", { month, email, override });
      toast.success(override ? "Override saved." : "Winner saved privately.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't select winner");
    } finally {
      setBusy(false);
    }
  };

  const notify = async () => {
    if (!window.confirm(`Send the congratulatory email to ${winnerData?.existing_winner?.email}?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/fan-club/winner/${month}/notify`);
      toast.success("Winner notified.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't notify");
    } finally {
      setBusy(false);
    }
  };

  const markSent = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/fan-club/winner/${month}/mark-sent`);
      toast.success("Gift marked as sent.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't update");
    } finally {
      setBusy(false);
    }
  };

  const existing = winnerData?.existing_winner;
  const suggested = winnerData?.suggested;
  const leaderboard = winnerData?.leaderboard || [];

  return (
    <div className="space-y-10" data-testid="admin-fan-club-panel">
      {/* SUMMARY */}
      <div className="grid sm:grid-cols-3 gap-4">
        <StatBox label="Total Members" value={members.length} />
        <StatBox label="Paid Members" value={paidCount} accent />
        <StatBox label="Fan Mail Received" value={fanMail.length} />
      </div>

      {/* MONTH PICKER + WINNER */}
      <section className="surface p-6 lg:p-8 space-y-5" data-testid="winner-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="eyebrow mb-2">Monthly Featured Supporter</p>
            <h3 className="font-serif text-2xl lg:text-3xl tracking-tighter font-light">
              Pick this month&apos;s winner
            </h3>
            <p className="text-[11px] text-white/45 mt-2 leading-relaxed max-w-md">
              Auto-pick runs on the 1st of each month at 09:00 UTC for the previous month.
              Your override always wins — once you Pick or Override here, the cron will skip that month.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="bg-transparent border border-white/15 rounded-none w-44 h-10 text-sm"
              data-testid="winner-month-picker"
            />
            <Button
              onClick={async () => {
                if (!window.confirm("Run the auto-pick cron now? It will pick last month's top-engaged member and email them.")) return;
                setBusy(true);
                try {
                  const { data } = await api.post("/admin/fan-club/winner/auto-pick");
                  if (data.ok && !data.skipped) {
                    toast.success(`Auto-picked ${data.winner.email} for ${data.month}.`);
                  } else if (data.skipped) {
                    toast(`Already picked for ${data.month}.`, { icon: "ℹ️" });
                  } else {
                    toast.message(`No eligible members for ${data.month}.`);
                  }
                  await load();
                } catch (err) {
                  toast.error(formatApiError(err) || "Auto-pick failed");
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy}
              className="btn-ghost-gold rounded-none h-10 px-4 text-[11px] whitespace-nowrap"
              data-testid="winner-run-autopick"
            >
              Run Auto-Pick Now
            </Button>
          </div>
        </div>

        {existing ? (
          <div
            className="surface p-5 space-y-3 ring-1 ring-[color:var(--gold)]/40"
            data-testid="winner-existing"
            style={{ background: "rgba(232,180,184,0.04)" }}
          >
            <div className="flex flex-wrap items-center gap-3">
              <Crown size={18} style={{ color: "var(--gold)" }} />
              <h4 className="font-serif text-xl tracking-tighter" style={{ color: "var(--gold)" }}>
                {existing.name || existing.email}
              </h4>
              <span className="text-[10px] uppercase tracking-[0.22em] text-white/45">
                {existing.email} · score {existing.engagement_score} · {existing.tier}
              </span>
              {existing.override && (
                <span className="text-[10px] uppercase tracking-[0.22em] text-amber-300">
                  Admin Override
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={notify}
                disabled={busy || existing.notified}
                className="btn-gold rounded-none h-10 px-5 text-xs"
                data-testid="winner-notify-btn"
              >
                {existing.notified ? "Email Sent ✓" : "Send Congrats Email"}
              </Button>
              <Button
                onClick={markSent}
                disabled={busy || existing.gift_sent}
                className="btn-ghost-gold rounded-none h-10 px-5 text-xs"
                data-testid="winner-mark-sent-btn"
              >
                {existing.gift_sent ? "Gift Sent ✓" : "Mark Gift As Sent"}
              </Button>
            </div>
            {existing.notified_at && (
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/35">
                Notified {new Date(existing.notified_at).toLocaleString()}
                {existing.gift_sent_at && ` · Gift sent ${new Date(existing.gift_sent_at).toLocaleString()}`}
              </p>
            )}
          </div>
        ) : (
          <div className="surface p-5 space-y-3" style={{ background: "rgba(232,180,184,0.04)" }}>
            <p className="text-sm text-white/65">
              No winner selected for {month} yet. Suggested:{" "}
              <strong style={{ color: "var(--gold)" }}>
                {suggested ? `${suggested.name || suggested.email} · score ${suggested.engagement_score}` : "no eligible members"}
              </strong>
            </p>
            {suggested && (
              <Button
                onClick={() => selectWinner(suggested.email, false)}
                disabled={busy}
                className="btn-gold rounded-none h-10 px-5 text-xs"
                data-testid="winner-accept-suggested"
              >
                Select {suggested.name || suggested.email}
              </Button>
            )}
          </div>
        )}

        {/* LEADERBOARD with manual override */}
        <div>
          <p className="eyebrow mb-3">Engagement Leaderboard · Top 20</p>
          <div className="surface divide-y divide-white/5 text-sm" data-testid="winner-leaderboard">
            {leaderboard.length === 0 && (
              <div className="p-4 text-white/50">No members yet.</div>
            )}
            {leaderboard.map((m, idx) => (
              <div key={m.email} className="grid grid-cols-12 gap-3 px-4 py-3 items-center">
                <div className="col-span-1 text-[10px] uppercase tracking-[0.2em] text-white/40">#{idx + 1}</div>
                <div className="col-span-4 truncate">
                  <div className="text-white/85">{m.name || "—"}</div>
                  <div className="text-[10px] text-white/40 truncate">{m.email}</div>
                </div>
                <div className="col-span-2 text-[11px] uppercase tracking-[0.2em]" style={{ color: m.tier === "paid" ? "var(--gold)" : "rgba(255,255,255,0.45)" }}>
                  {m.tier}
                </div>
                <div className="col-span-1 text-right font-serif tracking-tighter" style={{ color: "var(--gold)" }}>
                  {m.engagement_score}
                </div>
                <div className="col-span-2 text-[10px] text-white/40 text-center">
                  {m.fan_mail_count} mail
                </div>
                <div className="col-span-2 text-right">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => selectWinner(m.email, !!existing)}
                    className="text-[10px] uppercase tracking-[0.22em] text-white/55 hover:text-[color:var(--gold)] transition"
                    data-testid={`winner-pick-${m.email}`}
                  >
                    {existing ? "Override Pick" : "Pick"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WINNERS HISTORY */}
      <section className="surface p-6 lg:p-8 space-y-4" data-testid="winners-history">
        <p className="eyebrow">Winners History</p>
        {winners.length === 0 ? (
          <p className="text-sm text-white/50">No past winners yet.</p>
        ) : (
          <div className="space-y-2 text-sm">
            {winners.map((w) => (
              <div
                key={w.month}
                className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-l-2"
                style={{ borderColor: "var(--gold)" }}
              >
                <span className="font-serif tracking-tight">
                  <strong style={{ color: "var(--gold)" }}>{w.month}</strong>
                  <span className="text-white/55"> · {w.name || w.email}</span>
                </span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/45">
                  {w.notified ? "Notified" : "Pending notify"}
                  {" · "}
                  {w.gift_sent ? "Gift sent" : "Gift pending"}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* FAN MAIL */}
      <section className="surface p-6 lg:p-8 space-y-4" data-testid="admin-fan-mail">
        <div className="flex items-center justify-between">
          <p className="eyebrow">Fan Mail · Private</p>
          <span className="text-[10px] uppercase tracking-[0.2em] text-white/35">
            {fanMail.length} letters
          </span>
        </div>
        {fanMail.length === 0 ? (
          <p className="text-sm text-white/50">No fan mail yet.</p>
        ) : (
          <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
            {fanMail.map((f) => (
              <div key={f.id} className="surface p-4" data-testid={`fan-mail-row-${f.id}`}>
                <div className="flex items-baseline justify-between mb-2 gap-2">
                  <div className="flex items-center gap-2">
                    <strong style={{ color: "var(--gold)" }}>
                      {f.name || "Anonymous"}
                    </strong>
                    {f.kind === "voice" && (
                      <span
                        className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 border border-[color:var(--gold)]/40 rounded-full"
                        style={{ color: "var(--gold)" }}
                        data-testid={`fan-mail-voice-badge-${f.id}`}
                      >
                        <Mic size={10} />
                        Voice · {f.voice_duration ? `${Math.round(f.voice_duration)}s` : "note"}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] text-white/40 shrink-0">
                    {new Date(f.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="text-[11px] text-white/45 mb-2">{f.email}</div>
                <p className="text-sm text-white/85 whitespace-pre-wrap">{f.message}</p>
                {f.kind === "voice" && <VoiceMailPlayer id={f.id} />}
                {f.kind === "voice" && <VoiceTranscript mail={f} onRetried={load} />}
                {f.photo_url && (
                  <a
                    href={f.photo_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-2 text-[10px] uppercase tracking-[0.22em] text-[color:var(--gold)]"
                  >
                    View attachment ↗
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function VoiceMailPlayer({ id }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("nc_admin_token");
        const resp = await fetch(`${API}/admin/fan-mail/voice/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        createdUrl = URL.createObjectURL(blob);
        if (!cancelled) setSrc(createdUrl);
      } catch (e) {
        if (!cancelled) setError(e.message || "Couldn't load voice note");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [id]);

  if (loading) {
    return (
      <p className="text-[10px] uppercase tracking-[0.22em] text-white/35 mt-3" data-testid={`fan-mail-voice-loading-${id}`}>
        Loading voice note…
      </p>
    );
  }
  if (error) {
    return (
      <p className="text-[10px] uppercase tracking-[0.22em] text-red-300/70 mt-3" data-testid={`fan-mail-voice-error-${id}`}>
        Voice note unavailable ({error})
      </p>
    );
  }
  return (
    <audio
      controls
      src={src}
      className="w-full mt-3"
      data-testid={`fan-mail-voice-audio-${id}`}
    />
  );
}

function VoiceTranscript({ mail, onRetried }) {
  const [busy, setBusy] = useState(false);
  const status = mail.transcript_status || (mail.transcript ? "ok" : "queued");
  const retrigger = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/fan-mail/voice/${mail.id}/transcribe`);
      toast.success("Transcription queued. Refresh in a few seconds.");
      if (onRetried) await onRetried();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't trigger transcription");
    } finally {
      setBusy(false);
    }
  };

  if (status === "ok" && mail.transcript) {
    return (
      <div
        className="mt-3 border-l-2 border-[color:var(--gold)]/40 pl-3 py-1.5 italic"
        data-testid={`fan-mail-transcript-${mail.id}`}
      >
        <div className="flex items-center justify-between mb-1.5 not-italic">
          <span className="text-[9px] uppercase tracking-[0.25em] text-[color:var(--gold)]/85 inline-flex items-center gap-1.5">
            Whisper Transcript
          </span>
          <button
            type="button"
            onClick={retrigger}
            disabled={busy}
            className="text-[9px] uppercase tracking-[0.22em] text-white/35 hover:text-white/65 disabled:opacity-40"
            data-testid={`fan-mail-transcript-retry-${mail.id}`}
          >
            {busy ? "…" : "Re-Transcribe"}
          </button>
        </div>
        <p className="text-[13px] text-white/75 leading-relaxed">
          &ldquo;{mail.transcript}&rdquo;
        </p>
      </div>
    );
  }

  const labels = {
    queued: "Transcribing…",
    running: "Transcribing…",
    empty: "No speech detected",
    failed: "Transcription failed",
    skipped: "Transcription disabled",
  };
  const isFailed = status === "failed" || status === "skipped" || status === "empty";

  return (
    <div
      className="mt-3 flex items-center justify-between gap-3 text-[10px] uppercase tracking-[0.22em] text-white/40"
      data-testid={`fan-mail-transcript-status-${mail.id}`}
    >
      <span>
        {labels[status] || "Transcribing…"}
        {isFailed && mail.transcript_error ? ` · ${String(mail.transcript_error).slice(0, 60)}` : ""}
      </span>
      <button
        type="button"
        onClick={retrigger}
        disabled={busy}
        className="text-[10px] uppercase tracking-[0.22em] text-white/55 hover:text-[color:var(--gold)] disabled:opacity-40"
        data-testid={`fan-mail-transcript-retry-${mail.id}`}
      >
        {busy ? "…" : isFailed ? "Retry" : "Refresh"}
      </button>
    </div>
  );
}

function StatBox({ label, value, accent }) {
  return (
    <div className="surface p-5">
      <div
        className="font-serif text-3xl tracking-tighter"
        style={{ color: accent ? "var(--gold)" : "rgba(255,255,255,0.95)" }}
      >
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 mt-2">
        {label}
      </div>
    </div>
  );
}
